import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardBody } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import crudApi from "@serie3/common/api/crudApi";
import NewModal from "../../../shared/Modals/NewModal";
import FileDropzone from "../../../shared/Modals/FileDropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFolder, faFolderPlus, faFolderOpen, faEye, faHeart, faComment, faShare, faChartBar, faHouse, faUsers, faSpinner, faListCheck, faTrash, faXmark, faSquareCheck, faSquare } from "@fortawesome/free-solid-svg-icons";
import { useUploadSummary } from "../../../shared/Upload/useUploads";
import FileListTable from "./FileListTable";
import FileListGridItem from "./FileListGridItem";
import FolderTree from "./FolderTree";
import FolderBreadcrumb from "./FolderBreadcrumb";
import ToastMessages from "../../../../ToastMessages";
import { missingPreview } from "@serie3/mymediaplanner/Modules/Documents/Domain/getFile";
import {
  joinPath,
  parentOf,
  isInside,
  buildTree,
} from "@serie3/mymediaplanner/Modules/Documents/Domain/folderTree";
import {
  PERSONAL_SOURCE,
  tagFiles,
  locationOf,
  sameLocation,
  filesAt,
} from "@serie3/mymediaplanner/Modules/Documents/Domain/fileSources";
import { personalSource, loadGroupSources, copyGroupFile, GroupFileActions } from "./fileSources";

/** every file manager on the page shares one upload list; the target is captured per file */
const UPLOAD_SCOPE = "filemanager";

/** one file = one list row / card; the source key keeps ids of different locations apart */
const keyOf = (file) => `${file._source}-${file.id}`;

/**
 * Runs `worker` over `items`, at most `concurrency` at a time, and sorts the
 * outcomes: a worker that throws puts its item into `failed`.
 */
const runBatch = async (items, worker, concurrency = 4) => {
  const ok = [];
  const failed = [];
  let cursor = 0;
  const lane = async () => {
    while (cursor < items.length) {
      const item = items[cursor++];
      try {
        ok.push(await worker(item));
      } catch (e) {
        failed.push(item);
      }
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, lane));
  return { ok, failed };
};

const toolbarButtonStyle = (color, filled = false) => ({
  display: "inline-flex", alignItems: "center", gap: 6,
  padding: "6px 12px", borderRadius: 8, border: `1.5px solid ${filled ? color : "#e2e5ea"}`,
  background: filled ? color : "#fff", fontSize: 12, fontWeight: 600,
  color: filled ? "#fff" : "#374151", cursor: "pointer", fontFamily: "inherit",
});

const aggregateStats = (posts) => {
  const totals = { impressions: 0, likes: 0, comments: 0, shares: 0, clicks: 0 };
  for (const post of posts) {
    for (const stat of (post.stats ?? [])) {
      totals.impressions += parseInt(stat.impressions, 10) || 0;
      totals.likes      += parseInt(stat.likes, 10)       || 0;
      totals.comments   += parseInt(stat.comments, 10)    || 0;
      totals.shares     += parseInt(stat.shares, 10)      || 0;
      totals.clicks     += parseInt(stat.clicks, 10)      || 0;
    }
  }
  return totals;
};

const StatPill = ({ icon, color, label, value }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: `${color}10`, border: `1px solid ${color}30` }}>
    <FontAwesomeIcon icon={icon} style={{ color, fontSize: 13 }} />
    <div>
      <div style={{ fontSize: 11, color: "#9299a8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.6px" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{value.toLocaleString()}</div>
    </div>
  </div>
);

/** Everything a listing needs from one location, or null when the API refused it. */
const loadSource = async (source) => {
  const [filesResponse, foldersResponse] = await Promise.all([
    crudApi.justGet(source.endpoints.index),
    crudApi.justGet(source.endpoints.folders),
  ]);
  if (!filesResponse.ok) return null;
  return {
    source,
    files: tagFiles(source.normalizeFiles(filesResponse.data), source.key),
    folders: foldersResponse.ok ? foldersResponse.data.folders ?? [] : [],
  };
};

/**
 * The file manager over every location the caller can reach: their own files
 * and the file folder of each collaboration group they belong to, one root per
 * location in the folder tree. Files stay in their location; a group file gets
 * into the caller's own files (and so into their posts) by copying, which the
 * "Use" button of a picker does on its own.
 *
 * `sources` restricts the locations, e.g. a group's Files tab passes just that
 * group (see fileSources.jsx for the shape).
 */
const Filemanager = ({
  handleFileFunction = null,
  useGrid = false,
  showDelete = false,
  showAdd = false,
  /** locations to show; null = own files plus every collaboration group */
  sources = null,
  /** the page header is redundant inside a tab */
  hideTitle = false,
  /** text shown in the empty state */
  emptyText = null,
}) => {
  const { t } = useTranslation();
  const [resolvedSources, setResolvedSources] = useState(() => sources ?? []);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const uploadSummary = useUploadSummary(UPLOAD_SCOPE);
  const [showFilePreview, setShowFilePreview] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  // Files without a recognisable MIME type come back with type undefined/"".
  const previewMime = previewFile?.type || "";
  const [previewFileEntity, setPreviewFileEntity] = useState(null);
  const [textContent, setTextContent] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");
  const [fileLoading, setFileLoading] = useState(false);
  const [connectedPosts, setConnectedPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  // Folders per location key (flat API lists), and the open location.
  const [folders, setFolders] = useState({});
  const [current, setCurrent] = useState(() => locationOf(sources?.[0]?.key ?? PERSONAL_SOURCE));
  // Files waiting in the "move to folder" dialog (one from a card's Move, several from the batch bar).
  const [moveTargets, setMoveTargets] = useState([]);
  // Batch mode: cards and rows become checkboxes; the selection lives in the open location only.
  const [selectMode, setSelectMode] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [batchBusy, setBatchBusy] = useState(false);
  const lastToggled = useRef(null);

  const myFilesLabel = t("Backend.Files.Locations.Mine", { defaultValue: "My files" });
  const sourceOf = (key) => resolvedSources.find((source) => source.key === key) ?? null;
  const currentSource = sourceOf(current.source) ?? resolvedSources[0] ?? null;
  const fileSource = (file) => sourceOf(file?._source) ?? currentSource;
  const previewSource = previewFileEntity ? fileSource(previewFileEntity) : null;
  const labelOf = (source) => (source?.key === PERSONAL_SOURCE ? myFilesLabel : source?.label ?? "");

  const loadFolders = async (source) => {
    if (!source) return;
    const response = await crudApi.justGet(source.endpoints.folders);
    if (response.ok) setFolders((prev) => ({ ...prev, [source.key]: response.data.folders ?? [] }));
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const wanted = sources ?? [personalSource(myFilesLabel), ...(await loadGroupSources())];
      if (cancelled) return;
      const loaded = await Promise.all(wanted.map(loadSource));
      if (cancelled) return;
      // own files stay listed even when the API refused them (no paid plan: empty
      // manager, as before); a group whose listing failed is left out
      const usable = loaded
        .map((result, index) => result ?? (wanted[index].key === PERSONAL_SOURCE ? { source: wanted[index], files: [], folders: [] } : null))
        .filter(Boolean);
      setResolvedSources(usable.map((result) => result.source));
      setFiles(usable.flatMap((result) => result.files));
      setFolders(Object.fromEntries(usable.map((result) => [result.source.key, result.folders])));
      setCurrent(locationOf(usable[0]?.source.key ?? PERSONAL_SOURCE));
      setLoading(false);
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sources]);

  const visibleFiles = filesAt(files, current);
  const selectedFiles = visibleFiles.filter((file) => selected.has(keyOf(file)));
  const deletableSelection = selectedFiles.filter((file) => fileSource(file)?.canDelete(file));

  const openFolder = (sourceKey, path) => setCurrent(locationOf(sourceKey, path));

  // a selection only makes sense inside one folder
  useEffect(() => {
    setSelected(new Set());
    lastToggled.current = null;
  }, [current.source, current.path]);

  const toggleSelect = (file, index, shiftKey = false) => {
    setSelected((prev) => {
      const next = new Set(prev);
      const from = lastToggled.current;
      if (shiftKey && from !== null && from !== index) {
        const [lo, hi] = from < index ? [from, index] : [index, from];
        visibleFiles.slice(lo, hi + 1).forEach((entry) => next.add(keyOf(entry)));
      } else if (next.has(keyOf(file))) {
        next.delete(keyOf(file));
      } else {
        next.add(keyOf(file));
      }
      return next;
    });
    lastToggled.current = index;
  };

  const selectAll = () => setSelected(new Set(visibleFiles.map(keyOf)));
  const clearSelection = () => {
    setSelected(new Set());
    lastToggled.current = null;
  };
  const leaveSelectMode = () => {
    setSelectMode(false);
    clearSelection();
  };

  const createFolder = async () => {
    if (!currentSource) return;
    const name = window.prompt(t("Backend.Files.Folders.NamePrompt", { defaultValue: "Folder name" }));
    if (name === null) return;
    const path = joinPath(current.path, name);
    if (!path || path === current.path) return;
    const response = await crudApi.createNew(currentSource.endpoints.folders, { path }, null, true);
    if (response.status === 200) {
      await loadFolders(currentSource);
      setCurrent(locationOf(currentSource.key, response.data.path));
    } else {
      ToastMessages.Error(typeof response.data === "string" ? response.data : undefined);
    }
  };

  const renameFolder = async (sourceKey, node) => {
    const source = sourceOf(sourceKey);
    if (!source) return;
    const name = window.prompt(t("Backend.Files.Folders.RenamePrompt", { defaultValue: "New folder name" }), node.name);
    if (name === null) return;
    const to = joinPath(parentOf(node.path), name);
    if (!to || to === node.path) return;
    const response = await crudApi.edit(source.endpoints.folderRename, { from: node.path, to }, true);
    if (response.status === 200) {
      // files keep their ids; re-root the paths locally instead of reloading everything
      setFiles((prev) =>
        prev.map((file) =>
          file._source === sourceKey && isInside(file.folder_path, node.path)
            ? { ...file, folder_path: to + file.folder_path.slice(node.path.length) }
            : file
        )
      );
      if (current.source === sourceKey && isInside(current.path, node.path)) {
        setCurrent(locationOf(sourceKey, to + current.path.slice(node.path.length)));
      }
      await loadFolders(source);
    } else {
      ToastMessages.Error(typeof response.data === "string" ? response.data : undefined);
    }
  };

  const deleteFolder = async (sourceKey, node) => {
    const source = sourceOf(sourceKey);
    if (!source) return;
    const question = t("Backend.Files.Folders.DeleteConfirm", {
      defaultValue: 'Delete folder "{{name}}"? Only empty folders can be deleted.',
      name: node.name,
    });
    if (!window.confirm(question)) return;
    const response = await crudApi.del(source.endpoints.folderDelete, encodeURIComponent(node.path));
    if (response.status === 200) {
      if (current.source === sourceKey && isInside(current.path, node.path)) {
        setCurrent(locationOf(sourceKey, parentOf(node.path)));
      }
      setFolders((prev) => ({ ...prev, [sourceKey]: response.data.folders ?? [] }));
    } else if (response.status === 409) {
      ToastMessages.Error(
        t("Backend.Files.Folders.NotEmpty", { defaultValue: "Folder is not empty. Move or delete its files first." })
      );
    }
  };

  /**
   * Moves files (all of one location) into `path` of that location. Files
   * already there are skipped. One request per file; the list is updated
   * once with every file that made it.
   */
  const moveFiles = async (targets, path) => {
    const source = fileSource(targets[0]);
    if (!source) return;
    const todo = targets.filter(
      (file) =>
        file._source === source.key &&
        !sameLocation(locationOf(file._source, file.folder_path), locationOf(source.key, path))
    );
    if (todo.length === 0) return;
    setBatchBusy(true);
    const { ok, failed } = await runBatch(todo, async (file) => {
      const response = await crudApi.edit(source.endpoints.moveToFolder + file.id, { path: path ?? "" }, true);
      if (response.status !== 200) throw new Error(String(response.status));
      return { id: file.id, folderPath: response.data?.folder_path ?? null };
    });
    setBatchBusy(false);
    if (ok.length > 0) {
      const moved = new Map(ok.map((entry) => [entry.id, entry.folderPath]));
      setFiles((prev) =>
        prev.map((f) => (f._source === source.key && moved.has(f.id) ? { ...f, folder_path: moved.get(f.id) } : f))
      );
      await loadFolders(source);
    }
    if (failed.length > 0) {
      ToastMessages.Error(
        t("Backend.Files.Batch.MoveFailed", { defaultValue: "{{count}} file(s) could not be moved", count: failed.length })
      );
    } else if (ok.length > 1) {
      ToastMessages.SuccessfulSave(
        t("Backend.Files.Batch.Moved", { defaultValue: "{{count}} files moved", count: ok.length })
      );
    }
  };

  /** What a drop on the folder tree does: ids of the dragged cards, the folder they landed on. */
  const moveFilesById = async (ids, sourceKey, path) => {
    const targets = ids.map((id) => files.find((f) => f.id === id)).filter(Boolean);
    if (targets.length === 0) return;
    if (targets.some((file) => fileSource(file)?.key !== sourceKey)) {
      ToastMessages.NotAllowed(
        t("Backend.Files.Locations.NoCrossMove", {
          defaultValue: "Files stay in their location. Use \"Copy to my files\" to get a group file into your own files.",
        })
      );
      return;
    }
    await moveFiles(targets, path);
    clearSelection();
  };

  const openMove = (file) => setMoveTargets([file]);

  /**
   * Deletes several files after one confirmation. Files the caller may not
   * delete (someone else's group upload) are left out and reported.
   */
  const deleteFiles = async (targets) => {
    const allowed = targets.filter((file) => fileSource(file)?.canDelete(file));
    if (allowed.length === 0) return;
    const question = t("Backend.Files.Batch.DeleteConfirm", {
      defaultValue: "Delete {{count}} file(s)? This cannot be undone.",
      count: allowed.length,
    });
    if (!window.confirm(question)) return;
    setBatchBusy(true);
    const { ok, failed } = await runBatch(allowed, async (file) => {
      const response = await crudApi.del(fileSource(file).endpoints.delete, file.id);
      if (!response.ok) throw new Error(String(response.status));
      return file;
    });
    setBatchBusy(false);
    if (ok.length > 0) {
      const gone = new Set(ok.map(keyOf));
      setFiles((prev) => prev.filter((f) => !gone.has(keyOf(f))));
      await Promise.all([...new Set(ok.map((file) => fileSource(file)))].map(loadFolders));
    }
    clearSelection();
    const skipped = targets.length - allowed.length;
    if (failed.length > 0 || skipped > 0) {
      ToastMessages.Error(
        t("Backend.Files.Batch.DeleteFailed", {
          defaultValue: "{{count}} file(s) could not be deleted",
          count: failed.length + skipped,
        })
      );
    } else {
      ToastMessages.SuccessfulSave(
        t("Backend.Files.Batch.Deleted", { defaultValue: "{{count}} files deleted", count: ok.length })
      );
    }
  };

  /** Folder choices for the move dialog: the file's location root plus every folder in it, indented by depth. */
  const folderChoicesFor = (file) => {
    const source = fileSource(file);
    const rows = [{ path: null, depth: 0, name: labelOf(source) }];
    const walk = (nodes, depth) =>
      nodes.forEach((node) => {
        rows.push({ path: node.path, depth, name: node.name });
        walk(node.children, depth + 1);
      });
    walk(buildTree(folders[source?.key] ?? []), 1);
    return rows;
  };

  useEffect(() => {
    if (previewFile && previewMime.startsWith("text/")) {
      const loadTextContent = async () => {
        const response = await fetch(previewFile.url);
        const text = await response.text();
        setTextContent(text);
      };
      loadTextContent();
    }
  }, [previewFile]);

  const deleteFile = async (file) => {
    const source = fileSource(file);
    const response = await crudApi.del(source.endpoints.delete, file.id);
    if (response.ok) {
      setFiles((prev) => prev.filter((f) => !(f.id === file.id && f._source === file._source)));
      await loadFolders(source);
    } else if (response.status === 403) {
      ToastMessages.NotAllowed();
    } else {
      ToastMessages.Error();
    }
  };

  /** A copy of a group file, dropped into the root of the caller's own files. */
  const addCopy = (copy) => setFiles((prev) => [{ ...copy, _source: PERSONAL_SOURCE }, ...prev]);

  /**
   * What a picker's "Use" does. Group files are copied into the caller's own
   * files first, because posts and profiles only take files the caller owns;
   * the picker then receives the copy.
   */
  const pickFile = async (file) => {
    const source = fileSource(file);
    if (!source?.isGroup) {
      handleFileFunction(file);
      return;
    }
    const response = await copyGroupFile(source, file);
    if (response.ok) {
      const copy = { ...response.data, _source: PERSONAL_SOURCE };
      addCopy(copy);
      handleFileFunction(copy);
    } else if (response.status === 402) {
      ToastMessages.Error(t("Backend.Groups.Files.QuotaExceeded", { defaultValue: "Your file quota is used up" }));
    } else {
      ToastMessages.Error();
    }
  };
  const onPick = typeof handleFileFunction === "function" ? pickFile : null;

  /** Uploader chip and, outside a picker, "copy to my files" on group files. */
  const extraActionsFor = (file) => {
    const source = fileSource(file);
    if (!source?.isGroup) return null;
    return <GroupFileActions source={source} file={file} showCopy={!onPick} onCopied={addCopy} />;
  };

  const showFile = async (file) => {
    setPreviewFile(null);
    setTextContent(null);
    setPreviewTitle(file.original_name);
    setFileLoading(true);
    setShowFilePreview(true);
    setConnectedPosts([]);
    setPreviewFileEntity(file);

    const source = fileSource(file);
    const fileResponse = await crudApi.getBinary(source.endpoints.show + file.id);

    if (!fileResponse.ok) {
      // the row exists but its bytes are gone (404): keep the modal, show why
      setPreviewFile(missingPreview(fileResponse.status));
      setFileLoading(false);
      return;
    }

    const contentType = fileResponse.headers["content-type"];
    const blob = new Blob([fileResponse.data], { type: contentType });
    const url = URL.createObjectURL(blob);

    setPreviewFile({ url, type: contentType });
    setFileLoading(false);

    // usage stats only exist for the caller's own files
    if (!source.endpoints.imagePosts) return;
    setPostsLoading(true);
    const postsResponse = await crudApi.justGet(
      source.endpoints.imagePosts + file.id + "/posts"
    );
    if (postsResponse.ok) {
      setConnectedPosts(postsResponse.data.posts ?? []);
    }
    setPostsLoading(false);
  };

  const treeRoots = resolvedSources.map((source) => ({
    key: source.key,
    label: labelOf(source),
    isGroup: source.isGroup,
    folders: folders[source.key] ?? [],
  }));

  const emptyMessage =
    emptyText ??
    (currentSource?.isGroup
      ? t("Backend.Groups.Files.Empty", {
          defaultValue: "No files in this folder yet. Upload one for the whole group, or drag a file onto a folder to move it.",
        })
      : t("Backend.Files.Folders.Empty", { defaultValue: "No files in this folder yet. Upload one or drag a file onto a folder to move it." }));

  return (
    <>
      <NewModal
        show={showFilePreview}
        setShow={setShowFilePreview}
        title={previewTitle}
        size="xl"
      >
        {fileLoading ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 320, gap: 16 }}>
            <div style={{ width: 48, height: 48, borderRadius: "50%", border: "3px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
            <span style={{ color: "#9299a8", fontSize: 13 }}>Loading file…</span>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
            {/* Left — file preview */}
            <div style={{ flex: "1 1 0", minWidth: 0 }}>
              {previewFile?.missing && (
                <div style={{ padding: 32, textAlign: "center", color: "#9299a8", background: "#f8f9fa", borderRadius: 10 }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>⚠️</div>
                  <div style={{ fontWeight: 600, color: "#495057" }}>
                    {previewFile.status === 404 ? "This file is missing on the server." : "No preview available for this file."}
                  </div>
                  {previewFile.status === 404 && (
                    <div style={{ fontSize: 13, marginTop: 4 }}>The entry still exists, but its bytes were removed. Delete it or upload it again.</div>
                  )}
                </div>
              )}
              {previewFile && previewMime.startsWith("image/") && (
                <img src={previewFile.url} alt="Preview" style={{ maxWidth: "100%", borderRadius: 10 }} />
              )}
              {previewFile && previewMime === "application/pdf" && (
                <iframe src={previewFile.url} width="100%" height="600px" title="PDF Preview" />
              )}
              {previewFile && previewMime.startsWith("text/") && (
                <pre>{textContent}</pre>
              )}
              {previewFile && previewMime.startsWith("video/") && (
                <video controls width="100%">
                  <source src={previewFile.url} type="video/mp4" />
                </video>
              )}
              {previewFile &&
                !previewFile.missing &&
                !previewMime.startsWith("image/") &&
                previewMime !== "application/pdf" &&
                !previewMime.startsWith("text/") &&
                !previewMime.startsWith("video/") && (
                  <a href={previewFile.url} download={`file.${(previewMime.split("/")[1] || "bin")}`}>Download file</a>
                )}
            </div>

            {/* Right — details panel */}
            {previewFile && (
              <div style={{ width: 280, flexShrink: 0, display: "flex", flexDirection: "column", gap: 16 }}>

                {/* File info */}
                <div style={{ background: "#f8fafc", border: "1px solid #eaecf0", borderRadius: 12, padding: "14px 16px" }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#9299a8", marginBottom: 10 }}>File info</p>
                  <div style={{ fontSize: 13, color: "#374151", display: "flex", flexDirection: "column", gap: 4 }}>
                    <span><strong>Name:</strong> {previewFileEntity?.original_name}</span>
                    {previewFileEntity?.size && <span><strong>Size:</strong> {(previewFileEntity.size / 1024).toFixed(1)} KB</span>}
                    {previewFileEntity?.date && <span><strong>Uploaded:</strong> {new Date(previewFileEntity.date).toLocaleDateString()}</span>}
                  </div>
                </div>

                {/* Connected posts & stats */}
                {previewSource?.endpoints.imagePosts && (
                <div style={{ background: "#f8fafc", border: "1px solid #eaecf0", borderRadius: 12, padding: "14px 16px" }}>
                  <p style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#9299a8", marginBottom: 10 }}>
                    <FontAwesomeIcon icon={faChartBar} style={{ marginRight: 6 }} />
                    Usage & Stats
                  </p>
                  {postsLoading ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#9299a8", fontSize: 13 }}>
                      <div style={{ width: 16, height: 16, borderRadius: "50%", border: "2px dotted #038edc", animation: "dotted-spin 0.9s linear infinite" }} />
                      Loading…
                    </div>
                  ) : connectedPosts.length === 0 ? (
                    <p style={{ fontSize: 13, color: "#9299a8", margin: 0 }}>Not used in any post yet.</p>
                  ) : (
                    <>
                      <p style={{ fontSize: 13, color: "#374151", marginBottom: 12 }}>
                        Used in <strong>{connectedPosts.length}</strong> post{connectedPosts.length !== 1 ? "s" : ""}
                      </p>
                      {/* Aggregated stats */}
                      {(() => {
                        const totals = aggregateStats(connectedPosts);
                        return (
                          <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16 }}>
                            <StatPill icon={faEye}     color="#038edc" label="Impressions" value={totals.impressions} />
                            <StatPill icon={faHeart}   color="#e91e8c" label="Likes"       value={totals.likes} />
                            <StatPill icon={faComment} color="#20a56e" label="Comments"    value={totals.comments} />
                            <StatPill icon={faShare}   color="#f0a500" label="Shares"      value={totals.shares} />
                            {totals.clicks > 0 && (
                              <StatPill icon={faChartBar} color="#564ab1" label="Clicks" value={totals.clicks} />
                            )}
                          </div>
                        );
                      })()}
                      {/* Post list */}
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {connectedPosts.map((post) => {
                          const status = post.status?.toLowerCase() ?? "";
                          const statusColor = status === "published" ? "#51d28c" : status === "error" ? "#f34e4e" : "#9299a8";
                          const statusBg   = status === "published" ? "rgba(81,210,140,0.1)" : status === "error" ? "rgba(243,78,78,0.1)" : "rgba(146,153,168,0.1)";
                          const postStats  = aggregateStats([post]);
                          const hasStats   = postStats.impressions > 0 || postStats.likes > 0 || postStats.comments > 0 || postStats.shares > 0;
                          return (
                            <div key={post.id} style={{ padding: "10px 12px", borderRadius: 10, background: "#fff", border: "1px solid #eaecf0" }}>
                              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4, gap: 8 }}>
                                <div style={{ fontSize: 12, fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                                  {post.title || "—"}
                                </div>
                                <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: statusColor, background: statusBg, padding: "2px 7px", borderRadius: 20, flexShrink: 0 }}>
                                  {post.status ?? "—"}
                                </span>
                              </div>
                              {hasStats && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 6 }}>
                                  {postStats.impressions > 0 && (
                                    <span style={{ fontSize: 11, color: "#038edc", display: "flex", alignItems: "center", gap: 3 }}>
                                      <FontAwesomeIcon icon={faEye} style={{ fontSize: 10 }} /> {postStats.impressions.toLocaleString()}
                                    </span>
                                  )}
                                  {postStats.likes > 0 && (
                                    <span style={{ fontSize: 11, color: "#e91e8c", display: "flex", alignItems: "center", gap: 3 }}>
                                      <FontAwesomeIcon icon={faHeart} style={{ fontSize: 10 }} /> {postStats.likes.toLocaleString()}
                                    </span>
                                  )}
                                  {postStats.comments > 0 && (
                                    <span style={{ fontSize: 11, color: "#20a56e", display: "flex", alignItems: "center", gap: 3 }}>
                                      <FontAwesomeIcon icon={faComment} style={{ fontSize: 10 }} /> {postStats.comments.toLocaleString()}
                                    </span>
                                  )}
                                  {postStats.shares > 0 && (
                                    <span style={{ fontSize: 11, color: "#f0a500", display: "flex", alignItems: "center", gap: 3 }}>
                                      <FontAwesomeIcon icon={faShare} style={{ fontSize: 10 }} /> {postStats.shares.toLocaleString()}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
                )}
              </div>
            )}
          </div>
        )}
      </NewModal>
      <NewModal show={showFileUpload} setShow={setShowFileUpload}>
        {currentSource && (
          <FileDropzone
            scope={UPLOAD_SCOPE}
            uploadUrl={currentSource.endpoints.new}
            setName={false}
            // fires per finished file, possibly long after the modal was closed;
            // tag the document with the location it went to
            onUploaded={(doc) => {
              const sourceKey = currentSource.key;
              setFiles((prev) =>
                prev.some((file) => file.id === doc.id && file._source === sourceKey)
                  ? prev
                  : [{ ...doc, _source: sourceKey }, ...prev]
              );
            }}
            edit={false}
            additionalData={current.path ? { folder_path: current.path } : {}}
          />
        )}
      </NewModal>
      <NewModal
        show={moveTargets.length > 0}
        setShow={(show) => { if (!show) setMoveTargets([]); }}
        title={
          moveTargets.length > 1
            ? t("Backend.Files.Batch.MoveTitle", { defaultValue: "Move {{count}} files to folder", count: moveTargets.length })
            : t("Backend.Files.Folders.MoveTitle", { defaultValue: "Move file to folder" })
        }
        size="md"
      >
        {moveTargets.length > 0 && (
          <div role="listbox" style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <p
              style={{ fontSize: 13, color: "#9299a8", marginBottom: 8 }}
              title={moveTargets.map((file) => file.original_name).join("\n")}
            >
              {moveTargets.length > 1
                ? moveTargets.slice(0, 3).map((file) => file.original_name).join(", ") + (moveTargets.length > 3 ? ", …" : "")
                : moveTargets[0].original_name}
            </p>
            {folderChoicesFor(moveTargets[0]).map((choice) => {
              const here = moveTargets.every((file) => (file.folder_path ?? null) === choice.path);
              const rootIcon = fileSource(moveTargets[0])?.isGroup ? faUsers : faHouse;
              return (
                <button
                  key={choice.path ?? "root"}
                  type="button"
                  role="option"
                  aria-selected={here}
                  disabled={here || batchBusy}
                  onClick={async () => {
                    const targets = moveTargets;
                    setMoveTargets([]);
                    await moveFiles(targets, choice.path);
                    clearSelection();
                  }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, textAlign: "left",
                    padding: "8px 10px", paddingLeft: 10 + choice.depth * 16, borderRadius: 8,
                    border: "1.5px solid #eaecf0", background: here ? "#f3f4f6" : "#fff",
                    color: here ? "#9299a8" : "#374151", fontSize: 13, fontWeight: 500,
                    cursor: here ? "default" : "pointer", fontFamily: "inherit",
                  }}
                >
                  <FontAwesomeIcon icon={choice.path === null ? rootIcon : faFolder} style={{ color: choice.path === null ? "#038edc" : "#f0a500" }} />
                  {choice.name}
                </button>
              );
            })}
          </div>
        )}
      </NewModal>
      {!hideTitle && (
      <Row>
        <Col>
          <div className="page-title-box d-flex align-items-center justify-content-between">
            <h4 className="page-section-header" style={{ marginBottom: 0 }}>
              <span className="page-section-header-icon" style={{ background: "rgba(241,115,79,0.12)" }}>
                <FontAwesomeIcon icon={faFolder} style={{ color: "#f1734f" }} />
              </span>
              Filemanager
            </h4>
          </div>
        </Col>
      </Row>
      )}
      <div className="d-xl-flex">
        <div className="w-100">
          <div className="d-md-flex">
            <div className="filemanager-sidebar me-md-3" style={{ minWidth: 200, maxWidth: 220 }}>
              <div style={{ background: "#fff", border: "1px solid #eaecf0", borderRadius: 12, padding: "16px 12px", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
                <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: "#9299a8", marginBottom: 8, paddingLeft: 8 }}>
                  {t("Backend.Files.Folders.Title", { defaultValue: "Folders" })}
                </p>
                <FolderTree
                  roots={treeRoots}
                  current={current}
                  onSelect={openFolder}
                  onDropFile={moveFilesById}
                  onRename={renameFolder}
                  onDelete={deleteFolder}
                />
                <button
                  type="button"
                  onClick={createFolder}
                  disabled={!currentSource}
                  style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", marginTop: 8, padding: "8px 10px", borderRadius: 8, border: "1.5px dashed #d5d9e0", background: "#fff", color: "#6b7280", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#038edc")}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#d5d9e0")}
                >
                  <FontAwesomeIcon icon={faFolderPlus} />
                  {t("Backend.Files.Folders.New", { defaultValue: "New folder" })}
                </button>
                <hr style={{ borderColor: "#eaecf0", margin: "14px 0" }} />
                <button
                  type="button"
                  onClick={() => setShowFileUpload(true)}
                  disabled={!currentSource}
                  title={currentSource ? labelOf(currentSource) : undefined}
                  style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", padding: "9px 10px", borderRadius: 8, border: "1.5px solid #038edc", background: "#fff", color: "#038edc", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", transition: "background 0.15s ease" }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(3,142,220,0.06)")}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                >
                  + Upload File
                  {uploadSummary.active > 0 && (
                    <span className="uploadBadge" title={t("Backend.Upload.StillUploading", { defaultValue: "{{count}} file(s) still uploading", count: uploadSummary.active })}>
                      <FontAwesomeIcon icon={faSpinner} spin /> {uploadSummary.active}
                    </span>
                  )}
                  {uploadSummary.active === 0 && uploadSummary.failed > 0 && (
                    <span className="uploadBadge uploadBadgeFailed" title={t("Backend.Upload.Widget.Failed", { defaultValue: "{{count}} upload(s) failed", count: uploadSummary.failed })}>
                      {uploadSummary.failed}
                    </span>
                  )}
                </button>
              </div>
            </div>
            <div className="w-100">
              <Card>
                <CardBody>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
                    <FolderBreadcrumb
                      current={current.path}
                      rootLabel={labelOf(currentSource)}
                      rootIcon={currentSource?.isGroup ? faUsers : faHouse}
                      onSelect={(path) => openFolder(current.source, path)}
                    />
                    {!loading && !selectMode && visibleFiles.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setSelectMode(true)}
                        style={toolbarButtonStyle("#038edc")}
                        title={t("Backend.Files.Batch.SelectHint", { defaultValue: "Select several files to move or delete them together" })}
                      >
                        <FontAwesomeIcon icon={faListCheck} style={{ color: "#038edc" }} />
                        {t("Backend.Files.Batch.Select", { defaultValue: "Select" })}
                      </button>
                    )}
                  </div>
                  {selectMode && (
                    <div
                      role="toolbar"
                      aria-label={t("Backend.Files.Batch.Toolbar", { defaultValue: "Selected files" })}
                      style={{
                        display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
                        margin: "8px 0 14px", padding: "8px 12px", borderRadius: 10,
                        background: "#eaf5fc", border: "1px solid #b9dff5", fontSize: 13, color: "#0a4f7a",
                      }}
                    >
                      <b style={{ marginRight: 4 }}>
                        {t("Backend.Files.Batch.Selected", { defaultValue: "{{count}} selected", count: selectedFiles.length })}
                      </b>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0"
                        onClick={selectedFiles.length === visibleFiles.length ? clearSelection : selectAll}
                      >
                        <FontAwesomeIcon icon={selectedFiles.length === visibleFiles.length ? faSquare : faSquareCheck} className="me-1" />
                        {selectedFiles.length === visibleFiles.length
                          ? t("Backend.Files.Batch.SelectNone", { defaultValue: "Clear" })
                          : t("Backend.Files.Batch.SelectAll", { defaultValue: "Select all ({{count}})", count: visibleFiles.length })}
                      </button>
                      <span style={{ flex: 1 }} />
                      <button
                        type="button"
                        disabled={selectedFiles.length === 0 || batchBusy}
                        onClick={() => setMoveTargets(selectedFiles)}
                        style={{ ...toolbarButtonStyle("#f0a500"), opacity: selectedFiles.length === 0 || batchBusy ? 0.5 : 1 }}
                      >
                        <FontAwesomeIcon icon={faFolderOpen} style={{ color: "#f0a500" }} />
                        {t("Backend.Files.Folders.Move", { defaultValue: "Move" })}
                      </button>
                      {showDelete && (
                        <button
                          type="button"
                          disabled={deletableSelection.length === 0 || batchBusy}
                          onClick={() => deleteFiles(selectedFiles)}
                          title={
                            deletableSelection.length < selectedFiles.length
                              ? t("Backend.Files.Batch.DeleteSome", {
                                  defaultValue: "Only {{count}} of the selected files may be deleted by you",
                                  count: deletableSelection.length,
                                })
                              : undefined
                          }
                          style={{ ...toolbarButtonStyle("#f34e4e"), color: "#f34e4e", borderColor: "#fde8e8", opacity: deletableSelection.length === 0 || batchBusy ? 0.5 : 1 }}
                        >
                          <FontAwesomeIcon icon={batchBusy ? faSpinner : faTrash} spin={batchBusy} />
                          {t("Backend.Files.Buttons.DeleteFile")}
                          {deletableSelection.length > 0 && ` (${deletableSelection.length})`}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={leaveSelectMode}
                        disabled={batchBusy}
                        style={toolbarButtonStyle("#6b7280")}
                        title={t("Backend.Files.Batch.Done", { defaultValue: "Done" })}
                      >
                        <FontAwesomeIcon icon={faXmark} />
                        {t("Backend.Files.Batch.Done", { defaultValue: "Done" })}
                      </button>
                    </div>
                  )}
                  {loading ? (
                    <Row>
                      {[1, 2, 3, 4, 5, 6].map((i) => (
                        <Col md={4} key={i} className="mb-3">
                          <div style={{ border: "1.5px solid #eaecf0", borderRadius: 12, overflow: "hidden" }}>
                            <div className="skeleton" style={{ height: 160, borderRadius: 0 }} />
                            <div style={{ padding: "12px 14px" }}>
                              <div className="skeleton" style={{ height: 13, width: "70%", marginBottom: 8 }} />
                              <div className="skeleton" style={{ height: 11, width: "40%" }} />
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>
                  ) : !useGrid ? (
                    <Row>
                      <Col>
                        <FileListTable
                          files={visibleFiles}
                          deleteFile={(index, file) => deleteFile(file)}
                          showFile={showFile}
                          handleFileFunction={onPick}
                          moveFile={openMove}
                          selectable={selectMode}
                          isSelected={(file) => selected.has(keyOf(file))}
                          onToggleSelect={toggleSelect}
                        />
                      </Col>
                    </Row>
                  ) : visibleFiles.length === 0 ? (
                    <div style={{ textAlign: "center", padding: "48px 16px", color: "#9299a8", fontSize: 13 }}>
                      <FontAwesomeIcon icon={faFolder} style={{ fontSize: 32, color: "#e2e5ea", marginBottom: 12, display: "block", margin: "0 auto 12px" }} />
                      {emptyMessage}
                    </div>
                  ) : (
                    <Row>
                      {visibleFiles.map((file, index) => {
                        const source = fileSource(file);
                        const isSelected = selected.has(keyOf(file));
                        return (
                          <FileListGridItem
                            key={keyOf(file)}
                            file={file}
                            deleteFile={() => deleteFile(file)}
                            showFile={showFile}
                            handleFileFunction={onPick}
                            showDelete={showDelete && source.canDelete(file)}
                            showAdd={showAdd}
                            moveFile={openMove}
                            binaryEndpoint={source.endpoints.show}
                            extraActions={extraActionsFor}
                            selectable={selectMode}
                            selected={isSelected}
                            onToggleSelect={(event) => toggleSelect(file, index, event?.shiftKey)}
                            // dragging a selected card drags the whole selection
                            dragIds={isSelected ? selectedFiles.map((entry) => entry.id) : [file.id]}
                          />
                        );
                      })}
                    </Row>
                  )}
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Filemanager;
